import { DateTime } from "luxon";

// "1 zi", "5 zile", "20 de zile"
function amount(n, one, many) {
    if (n === 1) return `1 ${one}`;
    const de = n >= 20 && (n % 100 === 0 || n % 100 >= 20);
    return `${n} ${de ? "de " : ""}${many}`;
}

/**
 * "1 an, 2 luni și 3 zile în urmă" from a "dd-MM-yyyy" date (also "dd.MM.yyyy" or ISO); "azi" for today.
 * Calendar years/months/days: 25-09-2025 seen on 29-09-2026 is "1 an, 4 zile".
 */
export function calcTimeDifference(created_time) {
    const text = String(created_time || "").trim();
    let created = DateTime.fromFormat(text.slice(0, 10), "dd-MM-yyyy");
    if (!created.isValid) created = DateTime.fromFormat(text.slice(0, 10), "dd.MM.yyyy");
    if (!created.isValid) created = DateTime.fromISO(text);
    if (!created.isValid) return "";

    const diff = DateTime.now().startOf("day").diff(created.startOf("day"), ["years", "months", "days"]).toObject();
    const years = Math.max(0, Math.floor(diff.years || 0));
    const months = Math.max(0, Math.floor(diff.months || 0));
    const days = Math.max(0, Math.floor(diff.days || 0));
    if (!years && !months && !days) return "azi";

    let result = "";
    if (years > 0) {
        result += amount(years, "an", "ani");
        if (months > 0 || days > 0) result += ", ";
    }
    if (months > 0) {
        result += amount(months, "lună", "luni");
        if (days > 0) result += " și ";
    }
    if (days > 0) result += amount(days, "zi", "zile");

    return `${result} în urmă`;
}

export function formatCreatedAt(dateString) {
    const date = new Date(dateString);
    const formattedDate = date.toLocaleDateString("ro-MD", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    });
    return formattedDate;
}

export function sortTable(header) {
    if (header.sortable) {
        if (this.sortBy === header.value) {
            this.sortDesc = !this.sortDesc;
        } else {
            this.sortBy = header.value;
            this.sortDesc = false;
        }
    }
}

// `ignored`: keys whose values are not text a person would search for (photo ids and sizes)
export function filteredData(approvalData, search, ignored = []) {
    const searchTerm = search.toLowerCase().trim();
    return approvalData.filter((item) => {
        return Object.entries(item).some(([key, value]) => {
            if (ignored.includes(key)) return false;
            if (value !== null && typeof value !== "object") {
                return value.toString().toLowerCase().includes(searchTerm);
            } else if (value !== null && typeof value === "object") {
                // nested values may be null (a photo without its size, a department without a parent)
                return Object.values(value).some((nestedValue) => {
                    return nestedValue != null && nestedValue.toString().toLowerCase().includes(searchTerm);
                });
            }
            return false;
        });
    });
}

export function validateEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    // const domain = "usarb.md";
    // const isCorporateEmail = email.endsWith("@" + domain);
    return emailRegex.test(email);
}

export function validateIDNP(idnp) {
    const idnpLength = idnp.length;
    const idnpRegex = /^\d+$/;
    return idnpLength === 13 && idnpRegex.test(idnp);
}

export function validateTelephone(telephone) {
    const telephoneLength = telephone.length;
    const telephoneRegex = /^\d*$/;
    return telephoneLength === 9 && telephoneRegex.test(telephone);
}
export function validateLandline(landline) {
    const landlineRegex = /^\d+$/;

    // Ensure the input is numeric
    if (!landlineRegex.test(landline)) {
        return false;
    }

    // If the landline is exactly 3 digits, format it to '023152XXX'
    if (landline.length === 3) {
        return `023152${landline}`;
    }

    // If the landline is not exactly 9 digits after formatting, it's invalid
    if (landline.length !== 9) {
        return false;
    }

    return landline;
}

// "Corp 2" before "Corp 10"
export function sortNatural(items, key) {
    const text = (item) => String(item[key] == null ? "" : item[key]);
    return [...items].sort((a, b) => text(a).localeCompare(text(b), "ro", { numeric: true }));
}

/**
 * Where ?redirect= may send someone after logging in: only a page of this app ("/e/AB3K7Q2M?x=1"), else "/".
 * The value is decoded first; "//host", "/\host", schemes ("javascript:", "https:") and anything the browser
 * would resolve to another site are refused, and so is the login page itself.
 */
export function safeRedirect(target) {
    if (typeof target !== "string" || !target) return "/";
    let decoded = target;
    try {
        // twice-encoded values ("%252F%255C...") must not slip past the checks below
        for (let i = 0; i < 3 && /%[0-9a-f]{2}/i.test(decoded); i++) decoded = decodeURIComponent(decoded);
    } catch (e) {
        return "/";
    }
    // one leading "/", not "//" or "/\"; no control characters (the URL parser drops tabs / newlines)
    // eslint-disable-next-line no-control-regex
    const samePath = (value) => /^\/(?![/\\])/.test(value) && !/[\u0000-\u001f\u007f]/.test(value);
    if (!samePath(target) || !samePath(decoded)) return "/";
    let url;
    try {
        url = new URL(target, window.location.origin);
    } catch (e) {
        return "/";
    }
    if (url.origin !== window.location.origin) return "/";
    const path = url.pathname + url.search + url.hash;
    if (!samePath(path) || /^\/login(?:[/?#]|$)/i.test(url.pathname)) return "/";
    return path;
}

// v-dialog keeps its content between openings, so start each opening at the first field
export function scrollToTop(vm, ref = "body") {
    vm.$nextTick(() => {
        const body = vm.$refs[ref];
        const el = body && (body.$el || body);
        if (el) el.scrollTop = 0;
    });
}

// A price as people type it ("150,50", "150.50", "1 250,50"): a number, null when empty, NaN when it is not a price.
// Same rules as the server (requests/request/price.ts).
export function parsePrice(value) {
    if (value === null || value === undefined) return null;
    if (typeof value === "number") return Number.isFinite(value) && value >= 0 ? Math.round(value * 100) / 100 : NaN;
    let s = String(value).replace(/\s+/g, "");
    if (s === "") return null;
    const marks = s.match(/[.,]/g) || [];
    if (new Set(marks).size === 2) {
        const at = Math.max(s.lastIndexOf(","), s.lastIndexOf("."));
        s = s.slice(0, at).replace(/[.,]/g, "") + "." + s.slice(at + 1);
    } else if (marks.length > 1 || /^\d+[.,]\d{3}$/.test(s)) {
        s = s.replace(/[.,]/g, "");
    } else {
        s = s.replace(",", ".");
    }
    return /^\d+(\.\d+)?$/.test(s) ? Math.round(Number(s) * 100) / 100 : NaN;
}

export const PRICE_ERROR = "Prețul trebuie să fie un număr, de exemplu 150 sau 150,50";

export async function requestNotificationPermission() {
    if (Notification.permission !== "granted") {
        const permission = await Notification.requestPermission();
        if (permission !== "granted") {
            console.log("Notification permission denied");
            return permission;
        }
    }
    return Notification.permission;
}

// The server sends each step only to who acts next and to the author: { title, body, link, audience, responseId }
export function displayNotification(notification, router) {
    try {
        const { title, body, link, responseId } = notification;
        if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
        // one notification per step (or per item comment: `tag`) even with Suport open in several tabs
        const tag = notification.tag || (responseId ? `support-${responseId}` : undefined);
        const notificationObj = new Notification(title, { body, tag });
        notificationObj.onclick = () => {
            window.focus();
            const target = link || "/approval";
            const path = target.split("?")[0];
            // the page reads ?cerere= when it loads, so the same page is loaded again
            if (!router || router.currentRoute.path === path) window.location.assign(target);
            else router.push(target).catch(() => {});
            notificationObj.close();
        };
    } catch (error) {
        console.error("Error displaying notification:", error);
    }
}

export function setupWebSocket(vueInstance, userRole) {
    const socket = vueInstance.$socket;
    if (!socket) {
        console.error("WebSocket instance not found.");
        return;
    }
    // main.js and the home page both call this; listeners registered twice showed every notification twice
    if (socket.__supportListeners) return;
    socket.__supportListeners = true;

    socket.on('connect', () => {
        console.log("WebSocket connected");
    });

    socket.on('disconnect', () => {
        console.log("WebSocket disconnected");
    });

    socket.on('notificationMessage', message => {
        displayNotification(message, vueInstance.$router);
        // something now waits in this person's Aprobare: refresh the counter (App.vue)
        if (message && message.audience === "approver") vueInstance.$root.$emit("approval-queue-changed");
    });

    socket.on('error', error => {
        console.error("WebSocket error:", error);
    });
}

// export function setupWebSocket(vueInstance, userRole) {
//     const socket = vueInstance.$socket;
//     if (!socket) {
//         console.error("WebSocket instance not found.");
//         return;
//     }

//     socket.on('connect', () => {
//         console.log("WebSocket connected");
//     });

//     socket.on('disconnect', () => {
//         console.log("WebSocket disconnected");
//     });

//     socket.on('notificationMessage', message => {
//         const roleMap = {
//             1: 'usersef',
//             2: 'rector',
//             5: 'dtisef',
//             3: 'inginer',
//             6: 'inginer',
//         };

//         const allowedRole = roleMap[message.responseType];

//         if (allowedRole && userRole === allowedRole) {
//             console.log("Received message:", message);
//             displayNotification(message);
//         } else {
//             console.log("Notification not for this user role:", userRole);
//         }
//     });

//     socket.on('error', error => {
//         console.error("WebSocket error:", error);
//     });
// }
